import { AlertTriangleIcon, InfoIcon, PlusIcon, Trash2Icon } from 'lucide-react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { InputGroup, InputGroupAddon, InputGroupInput } from '@/components/ui/input-group';
import { Slider } from '@/components/ui/slider';
import { Textarea } from '@/components/ui/textarea';
import { STTModelInfo, STTModelSettings } from '@/shared/types/voiceProviders';

/**
 * Returns the unit suffix label for a numeric setting field based on its name convention.
 * `_secs` → "Seconds", `_ms` or `endpointing` → "Milliseconds", otherwise `false`.
 */
export const getUnitSuffix = (fieldPath: string, t: (key: string) => string): string | false =>
  fieldPath.includes('_secs')
    ? t('common.seconds')
    : (fieldPath.includes('_ms') || fieldPath.includes('endpointing')) && t('common.milliseconds');

interface STTDynamicSettingsProps {
  modelInfo: STTModelInfo;
  basePath: string;
}

interface DynamicSettingFieldProps {
  setting: STTModelSettings;
  basePath: string;
}

interface MultiSelectFieldProps {
  setting: STTModelSettings;
  fieldPath: string;
  label: string;
  tooltip: string;
}

const splitKeyword = (value: string) => {
  const [word, intensifier] = (value ?? '').split(':');
  return { word: word ?? '', intensifier: intensifier ?? '' };
};

const joinKeyword = (word: string, intensifier: string) =>
  intensifier ? `${word}:${intensifier}` : word;

function MultiSelectField({ setting, fieldPath, label, tooltip }: MultiSelectFieldProps) {
  const { t } = useTranslation();
  const { control } = useFormContext();
  const { fields, append, remove } = useFieldArray({ control, name: fieldPath });

  const isKeywordsField = setting.key === 'keywords';

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      append('' as never);
    }
  };

  return (
    <FormItem>
      {isKeywordsField ? (
        <div className="flex items-center gap-2">
          <FormLabel className="flex flex-1 items-center gap-1">
            {label}
            {tooltip && (
              <InfoTooltip content={tooltip} size="large">
                <InfoIcon className="size-3.5 text-foreground-muted" />
              </InfoTooltip>
            )}
          </FormLabel>
          <FormLabel className="w-20 text-center">
            {t('integrations.voice_common.transcription.intensifier')}
          </FormLabel>
          <div className="w-8" aria-hidden />
        </div>
      ) : (
        <FormLabel className="flex items-center gap-1">
          {label}
          {tooltip && (
            <InfoTooltip content={tooltip} size="large">
              <InfoIcon className="size-3.5 text-foreground-muted" />
            </InfoTooltip>
          )}
        </FormLabel>
      )}
      <div className="space-y-2">
        {fields.map((field, index) => (
          <FormField
            key={field.id}
            control={control}
            name={`${fieldPath}.${index}`}
            render={({ field: itemField }) => {
              const { word, intensifier } = splitKeyword(itemField.value);
              return (
                <FormItem>
                  <div className="flex items-center gap-2">
                    {isKeywordsField ? (
                      <>
                        <FormControl>
                          <Input
                            value={word}
                            onChange={(e) =>
                              itemField.onChange(joinKeyword(e.target.value, intensifier))
                            }
                            onBlur={() => itemField.onChange(joinKeyword(word.trim(), intensifier))}
                            onKeyDown={handleKeyDown}
                            className="flex-1"
                          />
                        </FormControl>
                        <Input
                          type="number"
                          min={0}
                          value={intensifier}
                          onChange={(e) => itemField.onChange(joinKeyword(word, e.target.value))}
                          onKeyDown={handleKeyDown}
                          placeholder="00"
                          className="w-20 text-center"
                        />
                      </>
                    ) : (
                      <FormControl>
                        <Input
                          {...itemField}
                          value={itemField.value ?? ''}
                          onBlur={() => itemField.onChange((itemField.value ?? '').trim())}
                          onKeyDown={handleKeyDown}
                          className="flex-1"
                        />
                      </FormControl>
                    )}
                    <Button
                      className="hover:text-destructive hover:[&_svg]:text-destructive"
                      type="button"
                      variant="ghost-neutral"
                      size="icon-sm"
                      onClick={() => remove(index)}
                      aria-label={t('common.remove')}
                    >
                      <Trash2Icon size={16} />
                    </Button>
                  </div>
                  <FormMessage />
                </FormItem>
              );
            }}
          />
        ))}
      </div>
      <Button type="button" variant="ghost-neutral" size="sm" onClick={() => append('' as never)}>
        <PlusIcon size={16} />
        {t(fields.length === 0 ? 'common.add' : 'common.add_another')}
      </Button>
      <FormMessage />
    </FormItem>
  );
}

function DynamicSettingField({ setting, basePath }: DynamicSettingFieldProps) {
  const { t } = useTranslation();
  const { control, setValue } = useFormContext();
  const fieldPath = `${basePath}.${setting.key}`;

  // Get translated label and description from translationKey (required)
  const label = t(setting.translationKey!);
  const description = t(`${setting.translationKey}_description`, { 0: setting.default });
  // Get tooltip if translation exists (returns key if not found, so we check for that)
  const tooltipKey = `${setting.translationKey}_tooltip`;
  const tooltipTranslation = t(tooltipKey);
  const tooltip = tooltipTranslation !== tooltipKey ? tooltipTranslation : '';
  // Optional warning shown when a boolean setting is enabled (same lookup pattern as tooltip).
  const warningKey = `${setting.translationKey}_warning`;
  const warningTranslation = t(warningKey);
  const warning = warningTranslation !== warningKey ? warningTranslation : '';

  switch (setting.type) {
    case 'slider':
      return (
        <FormField
          control={control}
          name={fieldPath}
          render={({ field }) => (
            <FormItem>
              <FormLabel>{label}</FormLabel>
              <FormControl>
                <Slider
                  tooltip
                  min={setting.min ?? 0}
                  max={setting.max ?? 1}
                  step={setting.step ?? 0.01}
                  minLabel={
                    setting.translationKey ? t(`${setting.translationKey}_min_label`) : undefined
                  }
                  maxLabel={
                    setting.translationKey ? t(`${setting.translationKey}_max_label`) : undefined
                  }
                  value={[field.value ?? (setting.default as number) ?? 0.5]}
                  onValueChange={(values) => setValue(fieldPath, values[0], { shouldDirty: true })}
                />
              </FormControl>
              {description && <FormDescription>{description}</FormDescription>}
            </FormItem>
          )}
        />
      );

    case 'boolean':
      return (
        <FormField
          control={control}
          name={fieldPath}
          render={({ field }) => (
            <FormItem className="flex flex-row items-start gap-3">
              <FormControl>
                <Checkbox
                  checked={field.value ?? (setting.default as boolean) ?? false}
                  onCheckedChange={(v) => setValue(fieldPath, v, { shouldDirty: true })}
                />
              </FormControl>
              <div>
                <FormLabel className="text-sm leading-none">{label}</FormLabel>
                {description && <FormDescription>{description}</FormDescription>}
                {warning && (field.value ?? (setting.default as boolean)) && (
                  <Alert variant="warning" className="mt-2">
                    <AlertTriangleIcon className="h-4 w-4" />
                    <AlertDescription>{warning}</AlertDescription>
                  </Alert>
                )}
              </div>
            </FormItem>
          )}
        />
      );

    case 'number': {
      const unitSuffix = getUnitSuffix(fieldPath, t);

      return (
        <FormField
          control={control}
          name={fieldPath}
          render={({ field }) => (
            <FormItem>
              <FormLabel>{label}</FormLabel>
              <FormControl>
                <InputGroup>
                  <InputGroupInput
                    type="number"
                    min={setting.min}
                    max={setting.max}
                    step={setting.step}
                    value={field.value ?? ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '') {
                        field.onChange(null);
                      } else if (!isNaN(Number(value))) {
                        field.onChange(Number(value));
                      }
                    }}
                  />
                  {unitSuffix && <InputGroupAddon align="inline-end">{unitSuffix}</InputGroupAddon>}
                </InputGroup>
              </FormControl>
              <FormMessage />
              {description && <FormDescription>{description}</FormDescription>}
            </FormItem>
          )}
        />
      );
    }

    case 'text': {
      // Use Textarea for prompt fields, Input for others
      const isTextArea = setting.key.includes('prompt') || setting.multiline;
      return (
        <FormField
          control={control}
          name={fieldPath}
          render={({ field }) => (
            <FormItem>
              <FormLabel className="flex items-center gap-1">
                {label}
                {tooltip && (
                  <InfoTooltip content={tooltip} size="large">
                    <InfoIcon className="size-3.5 text-foreground-muted" />
                  </InfoTooltip>
                )}
              </FormLabel>
              <FormControl>
                {isTextArea ? (
                  <Textarea {...field} value={field.value || ''} rows={7} className="min-h-24" />
                ) : (
                  <Input {...field} value={field.value || ''} placeholder={description} />
                )}
              </FormControl>
              <FormMessage />
              {description && <FormDescription>{description}</FormDescription>}
            </FormItem>
          )}
        />
      );
    }

    case 'select':
      return (
        <FormField
          control={control}
          name={fieldPath}
          render={({ field }) => (
            <FormItem>
              <FormLabel>{label}</FormLabel>
              <FormControl>
                <select
                  className="flex h-10 w-full rounded-md border border-stroke bg-background px-3 py-2 text-sm ring-offset-background focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:outline-none"
                  value={field.value ?? (setting.default as string) ?? ''}
                  onChange={(e) => setValue(fieldPath, e.target.value, { shouldDirty: true })}
                >
                  {setting.options?.map((option) => (
                    <option key={option.value} value={option.value}>
                      {t(option.label)}
                    </option>
                  ))}
                </select>
              </FormControl>
              {description && <FormDescription>{description}</FormDescription>}
            </FormItem>
          )}
        />
      );

    case 'multi-select':
      return (
        <MultiSelectField setting={setting} fieldPath={fieldPath} label={label} tooltip={tooltip} />
      );

    default:
      return null;
  }
}

export function STTDynamicSettings({ modelInfo, basePath }: STTDynamicSettingsProps) {
  return (
    <div className="space-y-6">
      {modelInfo.settings.map((setting) => (
        <DynamicSettingField key={setting.key} setting={setting} basePath={basePath} />
      ))}
    </div>
  );
}
